import React from 'react';
import { Box, FormControl, FormLabel, Input, FormErrorMessage, Flex, Button, Alert, AlertIcon, AlertTitle, AlertDescription, useToast, InputGroup, InputLeftElement } from '@chakra-ui/core';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';

import useLicense from '../utils/use-license';
import ModalBase from './ModalBase';


type FormData = {
    name: string;
    domain: string;
}

const FormSchema = yup.object<FormData>({
    name: yup.string().required("Namn måste vara angivet."),
    domain: yup.string().required("Domän måste vara angiven.")
});

interface FormProps {
    onFormSuccess: () => void;
}

const CreateDomainForm:React.FC<FormProps> = ({ onFormSuccess, children }) => {

    const { createDomainLicense } = useLicense();
    const toast = useToast();

    const [hasFormError, setHasFormError] = React.useState(false);
    const { handleSubmit, errors, register, formState } = useForm<FormData>({ resolver: yupResolver(FormSchema) });

    const nameRef = React.useRef<HTMLInputElement | null>(null);

    const handleOnSubmit = handleSubmit(async ({name, domain}) => {
        
        setHasFormError(false);

        const success = await createDomainLicense(name, domain);

        if (success !== null) {
            toast({
                position: "bottom-right",
                title: "Domänlicens tillagd.",
                description: "Domänlicensen blev tillagd med lyckat resultat.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            onFormSuccess();
        } else {
            setHasFormError(true);
        }
    });

    React.useEffect(() => {
        nameRef.current && nameRef.current.focus();
    }, []);

    return (
        <Box as="form" onSubmit={handleOnSubmit}>
            <FormControl isInvalid={errors.name !== undefined} mb={4}>
                <FormLabel htmlFor="name">Licensnamn</FormLabel>
                <Input
                    id="name"
                    name="name"
                    variant="filled"
                    placeholder="ex. Advania kommun"
                    ref={(e: HTMLInputElement) => {
                        register(e);
                        nameRef.current = e;
                    }}
                />
                <FormErrorMessage>
                    {errors.name && errors.name.message}
                </FormErrorMessage>
            </FormControl>
            <FormControl>
                <FormLabel htmlFor="domain">Domän</FormLabel>
                <InputGroup>
                    <InputLeftElement children="@" />
                    <Input
                        type="text"
                        placeholder="Domän ex. advania.se"
                        name="domain"
                        variant="filled"
                        ref={register}
                    />
                </InputGroup>
                <FormErrorMessage>
                    {errors.domain && errors.domain.message}
                </FormErrorMessage>
            </FormControl>
            <Flex
                align="center"
                justify="flex-end"
                pt={8}
                pb={2}
            >   
                <Button
                    variantColor="brand"
                    isLoading={formState.isSubmitting}
                    type="submit"
                    order={2}
                >
                    Lägg till
                </Button>
                {children}
            </Flex>
            {hasFormError && (
                <Alert status="error" borderRadius="2px">
                    <AlertIcon />
                    <Flex direction="column">
                    <AlertTitle>Något gick fel</AlertTitle>
                    <AlertDescription>Något gick fel när domänlicensen skulle skapas. Vänligen försök igen!</AlertDescription>
                    </Flex>
              </Alert>
            )}
        </Box>
    );
}

type CreateDomainModalType = {
    isOpen: boolean;
    onClose: () => void;
};

const CreateDomainModal: React.FunctionComponent<CreateDomainModalType> = ({ isOpen, onClose }) => {

    const handleOnSuccess = () => {
        onClose();
    }

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} modalTitle="Lägg till domänlicens">
            <CreateDomainForm onFormSuccess={handleOnSuccess}>
                <Button mr={4} order={0} variant="ghost" onClick={onClose}>
                    Avbryt
                </Button>
            </CreateDomainForm>
        </ModalBase>
    );
}

export default CreateDomainModal;